import { type ReactNode, type RefCallback, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { type ConversationViewModel } from '@/features/messages/ConversationViewModel'
import {
  ConversationMessage,
  ConversationMessageList,
  ConversationMessageListEmptyState,
  ConversationMessageListItemSkeleton,
  type ConversationMessageListProps,
  ConversationMessageListTimeItem,
  type ConversationMessageProps,
  ConversationMessageTextContent,
} from '.'
import { MinidownContent } from '@/components/MinidownContent'
import { useObservableValue } from '@/hooks/rx-hooks'
import { clock } from '@/utils/rx-util'
import {
  ChatListEventItemViewModel,
  type ChatListItemViewModel,
  ChatListMessageItemViewModel,
  ChatListTimeMarkerViewModel,
  type ChatListViewModel,
} from '@/features/messages/ChatListViewModel.ts'
import { useInfiniteScroll } from '@/hooks/infinite-scroll-hooks'
import {
  createSkeletonListItem,
  SkeletonListItemContext,
} from '@/components/Skeleton/SkeletonList.tsx'
import { useRootStore } from '@/contexts/root-store-context'
import type { ConversationMessageViewModel } from '@/features/messages/ConversationMessageViewModel'
import { match } from '@/utils/pattern-match-util'
import { useTranslate } from '@/hooks/translate'

/**
 * Render prop for a {@link ConversationMessage}.
 */
export type RenderConversationMessage<V> = (
  message: V,
  props: ConversationMessageProps,
) => ReactNode

/**
 * Render prop for a conversation event.
 */
export type RenderConversationEvent<E> = (
  event: E,
) => ReactNode | null | undefined

interface ConversationMessageListContainerProps<V, E>
  extends Omit<ConversationMessageListProps, 'children'> {
  /**
   * Overall view model for the conversation.
   */
  conversationVm: ConversationViewModel

  /**
   * View-model for the chat list items.
   */
  chatListVm: ChatListViewModel<ConversationMessageViewModel, V, E>

  /**
   * Render prop to render the {@link ConversationMessage} component.
   * If not specified, uses the default implementation.
   *
   * @param message
   * @param props
   */
  renderConversationMessage?: RenderConversationMessage<V>

  /**
   * Render prop to render a conversation event component.
   * If not specified, does not render events.
   *
   * @param event
   */
  renderEvent?: RenderConversationEvent<E>

  /**
   * Render-function for an empty state when there are no items.
   */
  renderEmptyState?: () => ReactNode

  /**
   * When `true`, will keep rendering skeletons until it turns `false`.
   */
  isPending?: boolean
}

/**
 * Container for {@link ConversationMessageList}.
 */
export const ConversationMessageListContainer = observer(
  function ConversationMessageListContainer<V, E>({
    chatListVm,
    conversationVm,
    renderConversationMessage,
    renderEvent,
    scrollingElement,
    isPending = false,
    ...props
  }: ConversationMessageListContainerProps<V, E>) {
    // Since we're using the RAC Collections API for message item rendering,
    // we'll need to implement skeletons + infinite scroll loading specifically
    // for this list.
    const { connectRef: connectLoadMoreTarget } = useInfiniteScroll({
      scroller: conversationVm.messagesScroller,
      activeDelay: 30,
      root: scrollingElement ?? undefined,
    })

    const skeletonCount = 8
    const skeletonItems = useMemo(() => {
      // Ideally, we'd use the total amount as well to only render remaining items,
      // but the API doesn't expose a total.
      return Array.from({ length: skeletonCount }).map(
        (_, index) => new SkeletonItem(index, skeletonCount),
      )
    }, [skeletonCount])

    const items = isPending
      ? skeletonItems
      : conversationVm.messagesScroller.isDone
        ? chatListVm.items.slice()
        : [...skeletonItems, ...chatListVm.items]

    return (
      <ConversationMessageList
        {...props}
        renderEmptyState={defaultRenderEmptyState}
        scrollingElement={scrollingElement}
        items={items}
      >
        {(item) =>
          item instanceof SkeletonItem ? (
            <ChatListItemSkeleton
              ref={connectLoadMoreTarget}
              id={`skeleton-${item.index}`}
              item={item}
            />
          ) : (
            <ChatListItemContainer
              // @ts-ignore: needed for React Aria's collection
              id={item.key}
              item={item}
              renderConversationMessage={renderConversationMessage}
              renderEvent={renderEvent}
            />
          )
        }
      </ConversationMessageList>
    )
  },
)

interface ChatListItemProps<V, E> {
  id: string
  item: ChatListItemViewModel<ConversationMessageViewModel, V, E>
  renderConversationMessage?: RenderConversationMessage<V>
  renderEvent?: RenderConversationEvent<E>
}

/**
 * Chat List Message Item View Model
 */
export const ChatListItemContainer = observer(function ChatListItemContainer<
  V,
  E,
>({ item, renderConversationMessage, renderEvent }: ChatListItemProps<V, E>) {
  const T = useTranslate()
  const {
    shell: { shellInfo },
  } = useRootStore()
  switch (true) {
    case item instanceof ChatListMessageItemViewModel: {
      const internalMessageVm = item.message

      const sender = internalMessageVm.message.sender
      const props: ConversationMessageProps = {
        id: internalMessageVm.key,
        displayName: sender.isCurrentUser
          ? 'You'
          : sender.isSystemRobot
            ? T('Web.Common.WorkspaceName', shellInfo.name)
            : sender.displayName,
        avatarUrl: sender.isSystemRobot
          ? shellInfo.systemRobotAvatarUrl
          : sender.avatarUrl,
        time: internalMessageVm.message.dateCreated,
        variant: sender.isCurrentUser ? 'outgoing' : 'incoming',
        avatarLayoutGroup: item.groupId,
        roleLabel: match(item.message.message.messageType, {
          AdminMessage: 'Admin',
          SystemMessage: 'System',
          [match.DEFAULT]: null,
        }),
        isFirstInGroup: item.isFirstInGroup,
        isLastInGroup: item.isLastInGroup,
        textValue: internalMessageVm.message.plainText,
        sendState:
          internalMessageVm.message.sendOperationState === 'Sending'
            ? 'sending'
            : internalMessageVm.message.sendOperationState === 'Failed'
              ? 'failed'
              : undefined,
        onEnterViewport: internalMessageVm.canMarkAsRead
          ? internalMessageVm.markRead
          : undefined,
        onRetryPress:
          internalMessageVm.message.sendOperationState === 'Failed'
            ? internalMessageVm.retrySend
            : undefined,
      }
      return renderConversationMessage
        ? renderConversationMessage(item.messageVm, props)
        : defaultRenderConversationMessage(internalMessageVm, props)
    }
    case item instanceof ChatListTimeMarkerViewModel: {
      return <ConversationMessageListTimeItemContainer date={item.date} />
    }
    case item instanceof ChatListEventItemViewModel: {
      return renderEvent?.(item.event) ?? null
    }

    default:
      return null
  }
})

interface DefaultConversationMessageContainerProps
  extends ConversationMessageProps {
  vm: ConversationMessageViewModel
}

/**
 * Container for the default implementation of a message component.
 */
export const DefaultConversationMessageContainer = observer(
  function DefaultConversationMessageContainer({
    vm,
    ...props
  }: DefaultConversationMessageContainerProps) {
    return (
      <ConversationMessage {...props} testId={`message-${vm.message.text}`}>
        <ConversationMessageTextContent
          testId={`message-content-${vm.message.text}`}
        >
          <MinidownContent value={vm.message.text} />
        </ConversationMessageTextContent>
      </ConversationMessage>
    )
  },
)

/**
 * Default implementation of {@link renderConversationMessage}.
 *
 * @param vm
 * @param props
 */
export function defaultRenderConversationMessage(
  vm: ConversationMessageViewModel,
  props: ConversationMessageProps,
) {
  return (
    <DefaultConversationMessageContainer
      id={vm.key}
      key={vm.key}
      vm={vm}
      {...props}
    />
  )
}

interface ConversationMessageListTimeItemContainerProps {
  date: Date
  id?: string
}

const tick$ = clock('minute')

/**
 * Container for {@link ConversationMessageListTimeItem}.
 * Ensures we don't rerender the whole list container when the clock ticks.
 */
export const ConversationMessageListTimeItemContainer = observer(
  function ConversationMessageListTimeItemContainer({
    date,
    id: _,
  }: ConversationMessageListTimeItemContainerProps) {
    const now = useObservableValue(tick$)
    return <ConversationMessageListTimeItem date={date} now={now} />
  },
)

interface ChatListItemSkeletonProps {
  id: string
  item: SkeletonItem
  ref?: RefCallback<HTMLElement>
}

/**
 * Chat List Item Skeleton
 */
export function ChatListItemSkeleton({ ref, item }: ChatListItemSkeletonProps) {
  const contextValue = useMemo(
    () =>
      // eslint-disable-next-line react-compiler/react-compiler
      createSkeletonListItem({
        index: item.index,
        attachRefTo: 'all',
        count: item.count,
        ref: ref,
      }),
    [item, ref],
  )

  return (
    <SkeletonListItemContext value={contextValue}>
      <ConversationMessageListItemSkeleton />
    </SkeletonListItemContext>
  )
}

/**
 * Placeholder item for a skeleton.
 */
class SkeletonItem {
  constructor(
    public readonly index: number,
    public readonly count: number,
  ) {}
}

/**
 * Default render function for empty state.
 */
function defaultRenderEmptyState() {
  return (
    <ConversationMessageListEmptyState
      title="No messages yet"
      description="When messages arrive, they'll appear here."
    />
  )
}
